import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Wrench, MapPin, BatteryCharging, ArrowRight, Phone } from "lucide-react";
import { api, ASSETS, CONTACT } from "@/lib/api";
import { FadeUp, Overline } from "@/components/FadeUp";
import ModelCard from "@/components/ModelCard";
import { Button } from "@/components/ui/button";

const TRUST_ICONS = [ShieldCheck, MapPin, Wrench];

export default function Home() {
  const [models, setModels] = useState([]);
  const { t } = useTranslation();
  const stats = t("stats", { returnObjects: true });
  const serviceCards = t("service.cards", { returnObjects: true });
  const brandBullets = t("brand.bullets", { returnObjects: true });

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-[#EAF0F6]">
        {/* Mobile: Text zuerst, Bild darunter */}
        <div className="md:hidden">
          <div className="px-6 pt-12 pb-8">
            <FadeUp>
              <Overline>{t("hero.overline")}</Overline>
              <h1 className="mt-4 text-4xl font-extrabold tracking-tighter leading-none text-[#0A2540]">
                {t("hero.title")}
              </h1>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                {t("hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-col gap-3">
                <Button asChild size="lg" className="rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3 w-full">
                  <Link to="/probefahrt" data-testid="hero-test-drive-btn">{t("cta.testDrive")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline"
                        className="rounded-md border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white px-8 py-3 bg-white/70 w-full">
                  <Link to="/modelle" data-testid="hero-models-btn">{t("hero.btnModels")}</Link>
                </Button>
              </div>
            </FadeUp>
          </div>
          <div className="overflow-hidden">
            <img src={ASSETS.heroFleet} alt="Skyworth Modellpalette"
                 className="w-full aspect-[16/8] object-cover object-center" />
          </div>
        </div>
        {/* Desktop: Bild mit Overlay-Text */}
        <div className="hidden md:block">
          <div className="max-h-[85vh] overflow-hidden">
            <img src={ASSETS.heroFleet} alt="Skyworth Modellpalette" data-testid="hero-image"
                 className="w-full h-full max-h-[85vh] object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-white/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
          <div className="absolute inset-x-0 bottom-0">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10 md:pb-16">
              <FadeUp>
                <Overline>{t("hero.overline")}</Overline>
                <h1 className="mt-4 text-5xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#0A2540] max-w-3xl">
                  {t("hero.title")}
                </h1>
                <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                  {t("hero.subtitle")}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Button asChild size="lg" className="rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3">
                    <Link to="/probefahrt">{t("cta.testDrive")}</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline"
                          className="rounded-md border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white px-8 py-3 bg-white/70">
                    <Link to="/modelle">{t("hero.btnModels")}</Link>
                  </Button>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8"
             data-testid="trust-bar">
          {Array.isArray(stats) && stats.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.08}>
              <p className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A2540]">{s.value}</p>
              <p className="mt-2 text-sm text-slate-600">{s.label}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Model showcase */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <FadeUp>
            <Overline>{t("showcase.overline")}</Overline>
            <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A2540] max-w-xl">
                {t("showcase.title")}
              </h2>
              <Link to="/modelle" data-testid="showcase-all-models-link"
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#0052CC] hover:gap-3 transition-all">
                {t("showcase.allLink")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="model-grid">
            {models.map((m, i) => (
              <FadeUp key={m.slug} delay={(i % 2) * 0.1}>
                <ModelCard model={m} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Brand */}
      <section className="bg-[#F4F7F9] border-y border-slate-200 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <Overline>{t("brand.overline")}</Overline>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A2540]">
              {t("brand.title")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              {t("brand.text")}
            </p>
            <ul className="mt-8 space-y-4">
              {Array.isArray(brandBullets) && brandBullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-700">
                  <BatteryCharging className="w-5 h-5 text-[#0052CC] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="border border-slate-200 bg-white p-2">
              <img src={ASSETS.trust} alt="Skyworth Fahrzeugpalette" data-testid="brand-image"
                   className="w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Brand film */}
      <section className="py-24 md:py-32 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <FadeUp>
            <Overline>{t("videoSection.overline")}</Overline>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A2540] max-w-2xl">
              {t("videoSection.title")}
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("videoSection.text")}
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <video
              src="/videos/ce11.mp4"
              poster="/images/ce11-241f582cb25d4218bbd9fef2684b69e2.jpeg"
              controls
              preload="metadata"
              data-testid="home-brand-video"
              className="mt-10 w-full border border-slate-200 bg-black aspect-video"
            />
          </FadeUp>
        </div>
      </section>

      {/* B2B / Fleet */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <FadeUp className="order-2 lg:order-1">
            <div className="border border-slate-200 bg-[#EAF0F6] p-2">
              <img src={ASSETS.fleet} alt="Skyworth Flottenlösungen" data-testid="fleet-image"
                   className="w-full object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="order-1 lg:order-2">
            <Overline>{t("fleetHome.overline")}</Overline>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A2540]">
              {t("fleetHome.title")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              {t("fleetHome.text")}
            </p>
            <Button asChild className="mt-8 rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3">
              <Link to="/flotten" data-testid="fleet-cta-btn">{t("fleetHome.button")}</Link>
            </Button>
          </FadeUp>
        </div>
      </section>

      {/* Trust & Service */}
      <section className="bg-[#0A2540] text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <FadeUp>
            <p className="text-sm tracking-[0.2em] uppercase font-semibold text-sky-300">{t("service.overline")}</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
              {t("service.title")}
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {Array.isArray(serviceCards) && serviceCards.map((c, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <FadeUp key={c.title} delay={i * 0.1} className="bg-[#0A2540] p-8 md:p-10">
                  <Icon className="w-8 h-8 text-sky-400" />
                  <h3 className="mt-5 text-xl font-semibold">{c.title}</h3>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{c.text}</p>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <img src={ASSETS.alps} alt="Strasse durch die Schweizer Alpen"
             className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A2540]/80" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white max-w-2xl">
              {t("ctaSection.title")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-200 max-w-xl leading-relaxed">
              {t("ctaSection.text")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-md bg-white text-[#0A2540] hover:bg-slate-100 px-8 py-3">
                <Link to="/probefahrt" data-testid="cta-test-drive-btn">{t("ctaSection.btnPrimary")}</Link>
              </Button>
              <a href={CONTACT.phoneHref} data-testid="cta-phone-btn"
                 className="flex items-center gap-2 px-8 py-3 border border-white/40 text-white font-semibold rounded-md hover:bg-white/10 transition-colors">
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
